/**
 * Hamburger Menu Component
 * 
 * A slide-out menu containing navigation items moved from the sidebar.
 * Placed in the top-left corner, replacing the AI Chat icon.
 * 
 * @module HamburgerMenu
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Menu,
  Settings,
  Eye,
  BookOpen,
  Volume2,
  LogIn,
  LogOut,
  Globe,
  PenSquare,
  Users,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface HamburgerMenuProps {
  onRestMode?: () => void;
  headerFocusIndex?: number; // For scanning support
}

const menuItems = [
  { id: "settings", labelKey: "settings", icon: Settings, href: "/settings" },
  { id: "userContacts", labelKey: "userContacts", icon: Users, href: "/user-contacts" },
  { id: "control", labelKey: "control", icon: Eye, href: "/control" },
  { id: "editBoards", labelKey: "editBoards", icon: PenSquare, href: "/board-editor" },
  { id: "volume", labelKey: "volume", icon: Volume2, href: "/volume" },
  { id: "guide", labelKey: "guide", icon: BookOpen, href: "/guide" },
];

// Scanning color - always orange
const SCAN_FOCUS_COLOR = 'rgb(251, 146, 60)';

export function HamburgerMenu({ onRestMode, headerFocusIndex = -1 }: HamburgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t, isRTL, dir, lang, setLanguage } = useCaregiverTranslation();

  const handleItemClick = (item: typeof menuItems[0]) => {
    if (item.href) {
      navigate(item.href);
    }
    setIsOpen(false);
  };

  const handleAuthClick = () => {
    if (user) {
      signOut();
    } else {
      navigate("/auth");
    }
    setIsOpen(false);
  };

  const handleLanguageToggle = () => {
    setLanguage(lang === "he" ? "en" : "he");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          style={headerFocusIndex === 1 ? {
            boxShadow: `0 0 0 4px ${SCAN_FOCUS_COLOR}, 0 0 0 8px rgba(251, 146, 60, 0.3), 0 0 20px rgba(251, 146, 60, 0.4)`,
            transform: 'scale(1.05)',
          } : undefined}
          className={cn(
            "w-10 h-10 rounded-xl transition-all border-2 p-0 flex items-center justify-center",
            "bg-primary/10 text-primary border-primary/30",
            "hover:bg-accent/20 hover:text-accent hover:border-accent"
          )}
          aria-label={isRTL ? "תפריט" : "Menu"}
        >
          <Menu className="!w-5 !h-5" />
        </button>
      </SheetTrigger>
      <SheetContent 
        side="left"
        className="w-[340px] sm:w-[380px] bg-sidebar border-sidebar-border [&>button]:w-8 [&>button]:h-8 [&>button>svg]:w-5 [&>button>svg]:h-5 flex flex-col z-[1000000]"
        dir={dir}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{isRTL ? "תפריט" : "Menu"}</SheetTitle>
        </SheetHeader>
        
        <nav className="flex flex-col gap-2 mt-8 flex-1 overflow-y-auto min-h-0">
          {menuItems.map((item) => {
            const Icon = item.icon;
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={cn(
                  "w-full flex items-center gap-3 py-4 px-4 rounded-xl",
                  "transition-all duration-200",
                  "text-sidebar-foreground hover:bg-primary/20 hover:text-primary",
                  "text-right"
                )}
              >
                <Icon className="w-6 h-6 flex-shrink-0" />
                <span className="text-base font-medium">
                  {t(item.labelKey as any)}
                </span>
              </button>
            );
          })}
          
          {/* Divider */}
          <div className="my-2 border-t border-sidebar-border" />
          
          {/* Auth Button */}
          <button
            onClick={handleAuthClick}
            className={cn(
              "w-full flex items-center gap-3 py-4 px-4 rounded-xl",
              "transition-all duration-200",
              "text-sidebar-foreground hover:bg-primary/20 hover:text-primary"
            )}
          >
            {user ? (
              <>
                <LogOut className="w-6 h-6 flex-shrink-0" />
                <span className="text-base font-medium">{t("logout")}</span>
              </>
            ) : (
              <>
                <LogIn className="w-6 h-6 flex-shrink-0" />
                <span className="text-base font-medium">{t("login")}</span>
              </>
            )}
          </button>
        </nav>

        {/* Language Toggle - Bottom of menu */}
        <div className="mt-auto pt-4 border-t border-sidebar-border flex justify-center">
          <button
            onClick={handleLanguageToggle}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full",
              "bg-card/80 backdrop-blur-sm border border-border",
              "hover:bg-card transition-colors text-sm font-medium"
            )}
          >
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span>{lang === "he" ? "English" : "עברית"}</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
